import { useContext, useEffect, useMemo, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { WidgetContractsChartUI } from "./ui"
import { DEFAULT_WIDGET_CONTRACT_STATUS_COLORS, IWidgetContractsChartActions, IWidgetContractsChartUI } from "./types"

import { getStatsContractsDaysModskill as requestStatsContractsDaysModskill, getStatsContractsDaysYaskill as requestStatsContractsDaysYaskill } from "@api/dashboard/api"
import { useDict } from "@api/dict/state"
import { IContractsStatusesList } from "@api/dict/state.types"
import { useMonitoring } from "@api/dashboard/state"
import { CommonStatsContractsType, IStatsContractsDays, IStatsContractsMonth, IStatsContractsYear } from "@api/dashboard/state.types"
import { useValue } from "@hooks/useValue"
import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"

const PREVIOUS_MONTH_START_DAY = 15

const getContractStatusColor = (items: IContractsStatusesList[], contractStatusID: number, fallback: string) => {
    const item = items.find((status) => status.contractStatusID === contractStatusID)

    return item?.contractStatusColor?.trim() || fallback
}

const getPreviousMonthPeriod = (year: number, month: number) => {
    const previousMonthDate = new Date(year, month - 2, 1)

    return {
        year: previousMonthDate.getFullYear(),
        month: previousMonthDate.getMonth() + 1,
    }
}

const getPreviousMonthDaysFromFifteenth = (previousItems: IStatsContractsDays[] = []) => {
    return previousItems.filter((item) => item.day >= PREVIOUS_MONTH_START_DAY)
}

const mergeItemsWithPreviousMonthTail = (currentItems: IStatsContractsDays[] = [], previousItems: IStatsContractsDays[] = []) => {
    return [...currentItems, ...getPreviousMonthDaysFromFifteenth(previousItems)]
}

/** Контейнер виджета - Статистика договоров Chart */
export const WidgetContractsChartContainer = ({ getMonthName, source, title }: IWidgetContractsChartActions) => {
    const notify = useContext(NotificationContext)
    const storagePrefix = `${source}_contracts_chart`
    const isModskill = source === 'modskill'

    const txtCurrentYear = useValue<string>('')
    const txtCurrentMonth = useValue<string>('')
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const dayRequestVersionRef = useRef(0)

    const itemsYear = useValue<IStatsContractsYear[]>([])
    const itemsMonth = useValue<IStatsContractsMonth[]>([])
    const itemsDays = useValue<IStatsContractsDays[]>([])
    const contractStatuses = useValue<IContractsStatusesList[]>([])
    const contractStatusColors = useValue(DEFAULT_WIDGET_CONTRACT_STATUS_COLORS)
    const showStatisticsData = useValue<boolean>(localStorage.getItem(`${storagePrefix}_show_statistics_data`) === 'true')

    const typeStats = useValue<'day' | 'month' | 'year'>(
        (localStorage.getItem(`${storagePrefix}_type`) as 'day' | 'month' | 'year') || 'day'
    )
    const yearDetailStatsContracts = useValue<string>(
        localStorage.getItem(`${storagePrefix}_year`) || `${new Date().getFullYear()}`
    )
    const monthDetailStatsContracts = useValue<string>(
        localStorage.getItem(`${storagePrefix}_month`) || `${new Date().getMonth() + 1}`
    )

    const {
        actionGetStatsContractsYearsModskill,
        actionGetStatsContractsYearsYaskill,
        actionGetStatsContractsMonthModskill,
        actionGetStatsContractsMonthYaskill,
        actionGetStatsContractsDaysModskill,
        actionGetStatsContractsDaysYaskill,
    } = useMonitoring(useShallow((state) => ({
        actionGetStatsContractsYearsModskill: state.actionGetStatsContractsYearsModskill,
        actionGetStatsContractsYearsYaskill: state.actionGetStatsContractsYearsYaskill,
        actionGetStatsContractsMonthModskill: state.actionGetStatsContractsMonthModskill,
        actionGetStatsContractsMonthYaskill: state.actionGetStatsContractsMonthYaskill,
        actionGetStatsContractsDaysModskill: state.actionGetStatsContractsDaysModskill,
        actionGetStatsContractsDaysYaskill: state.actionGetStatsContractsDaysYaskill,
    })))
    const { actionGetContractsStatuses } = useDict(useShallow((state) => ({
        actionGetContractsStatuses: state.actionGetContractsStatuses,
    })))

    const actionGetYears = isModskill ? actionGetStatsContractsYearsModskill : actionGetStatsContractsYearsYaskill
    const actionGetMonth = isModskill ? actionGetStatsContractsMonthModskill : actionGetStatsContractsMonthYaskill
    const actionGetDays = isModskill ? actionGetStatsContractsDaysModskill : actionGetStatsContractsDaysYaskill
    const requestGetDays = isModskill ? requestStatsContractsDaysModskill : requestStatsContractsDaysYaskill

    const loadDayStatistics = async (year: number, month: number, isLoadingDisplay: boolean = true) => {
        const requestVersion = ++dayRequestVersionRef.current
        const response = await actionGetDays(year, month, isLoadingDisplay)

        if (response.status === 200) {
            if (requestVersion !== dayRequestVersionRef.current) return

            const previousMonthPeriod = getPreviousMonthPeriod(year, month)
            const previousMonthResponse = await requestGetDays(previousMonthPeriod.year, previousMonthPeriod.month, !isLoadingDisplay)
            if (requestVersion !== dayRequestVersionRef.current) return

            const currentItems = response.data?.items || []
            const previousItems = previousMonthResponse.status === 200
                ? previousMonthResponse.data?.items || []
                : []

            itemsDays.setValue(mergeItemsWithPreviousMonthTail(currentItems, previousItems))
        }

        if (response.status !== 200 && response.status !== 500) {
            notify && notifyOpen(response.message, 'error', 2000, notify)
        }
    }

    useEffect(() => {
        actionGetContractsStatuses().then((res) => {
            contractStatuses.setValue(res)
            contractStatusColors.setValue({
                signing: getContractStatusColor(res, 2, DEFAULT_WIDGET_CONTRACT_STATUS_COLORS.signing),
                education: getContractStatusColor(res, 4, DEFAULT_WIDGET_CONTRACT_STATUS_COLORS.education),
                completed: getContractStatusColor(res, 5, DEFAULT_WIDGET_CONTRACT_STATUS_COLORS.completed),
                cancel: getContractStatusColor(res, 6, DEFAULT_WIDGET_CONTRACT_STATUS_COLORS.cancel),
            })
        })

        actionGetYears().then((res) => {
            if (res.status === 200 && res.data?.items) itemsYear.setValue(res.data.items)
            if (res.status !== 200 && res.status !== 500) notify && notifyOpen(res.message, 'error', 2000, notify)
        })

        actionGetMonth().then((res) => {
            if (res.status === 200 && res.data?.items) itemsMonth.setValue(res.data.items)
            if (res.status !== 200 && res.status !== 500) notify && notifyOpen(res.message, 'error', 2000, notify)
        })

    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (typeStats.value !== 'day') return

        let currentMonth = Number(monthDetailStatsContracts.value)
        const listMonthsOnYear = itemsMonth.value.filter((item) => item.year === Number(yearDetailStatsContracts.value))
        const listMonthOnMonth = listMonthsOnYear.filter((item) => item.month === currentMonth)

        if (listMonthOnMonth.length === 0 && listMonthsOnYear.length > 0) {
            currentMonth = listMonthsOnYear[0].month
            monthDetailStatsContracts.setValue(`${listMonthsOnYear[0].month}`)
        }

        loadDayStatistics(Number(yearDetailStatsContracts.value), currentMonth)
    }, [typeStats.value, yearDetailStatsContracts.value, monthDetailStatsContracts.value, itemsMonth.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        localStorage.setItem(`${storagePrefix}_type`, typeStats.value)
    }, [typeStats.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        txtCurrentYear.setValue(`${yearDetailStatsContracts.value}`)
        localStorage.setItem(`${storagePrefix}_year`, yearDetailStatsContracts.value)
    }, [yearDetailStatsContracts.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        txtCurrentMonth.setValue(getMonthName(Number(monthDetailStatsContracts.value)))
        localStorage.setItem(`${storagePrefix}_month`, monthDetailStatsContracts.value)
    }, [monthDetailStatsContracts.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        localStorage.setItem(`${storagePrefix}_show_statistics_data`, `${showStatisticsData.value}`)
    }, [showStatisticsData.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            loadDataAutomatically()
        }, 30000)

        return () => {
            dayRequestVersionRef.current += 1
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [typeStats.value, yearDetailStatsContracts.value, monthDetailStatsContracts.value]) // eslint-disable-line react-hooks/exhaustive-deps

    const totalsYears = useMemo(() => {
        if (!itemsYear.value || itemsYear.value.length === 0) return undefined

        return itemsYear.value.reduce<CommonStatsContractsType>((acc, curr) => ({
            signing: acc.signing + curr.signing,
            education: acc.education + curr.education,
            completed: acc.completed + curr.completed,
            cancel: acc.cancel + curr.cancel,
            own: acc.own + curr.own,
            total: acc.total + curr.total,
        }), { signing: 0, education: 0, completed: 0, cancel: 0, own: 0, total: 0 })
    }, [itemsYear.value])

    const totalsDays = useMemo(() => {
        if (!itemsDays.value || itemsDays.value.length === 0) return undefined

        return itemsDays.value.reduce<CommonStatsContractsType>((acc, curr) => ({
            signing: acc.signing + curr.signing,
            education: acc.education + curr.education,
            completed: acc.completed + curr.completed,
            cancel: acc.cancel + curr.cancel,
            own: acc.own + curr.own,
            total: acc.total + curr.total,
        }), { signing: 0, education: 0, completed: 0, cancel: 0, own: 0, total: 0 })
    }, [itemsDays.value])

    const loadDataAutomatically = () => {
        switch (typeStats.value) {
            case 'day': {
                loadDayStatistics(Number(yearDetailStatsContracts.value), Number(monthDetailStatsContracts.value), false)
                break
            }

            case 'month': {
                actionGetMonth(false).then((res) => {
                    if (res.status === 200 && res.data?.items) itemsMonth.setValue(res.data.items)
                })
                break
            }

            case 'year': {
                actionGetYears(false).then((res) => {
                    if (res.status === 200 && res.data?.items) itemsYear.setValue(res.data.items)
                })
                break
            }
        }
    }

    const propsToUI: IWidgetContractsChartUI = {
        txtCurrentYear,
        txtCurrentMonth,
        showStatisticsData,
        contractStatuses: contractStatuses.value,
        contractStatusColors: contractStatusColors.value,
        totalsYears,
        totalsDays,
        typeStats,
        yearDetailStatsContracts,
        monthDetailStatsContracts,
        itemsYear: itemsYear.value,
        itemsMonth: itemsMonth.value,
        itemsDays: itemsDays.value,
        getMonthName,
        title,
    }

    return <WidgetContractsChartUI {...propsToUI} />
}
